import React, { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { api } from "../lib/api";
import { Send, Loader2, Sparkles, FileText, BookOpen, Volume2, VolumeX, Paperclip, X, Image as ImageIcon, File as FileIcon } from "lucide-react";
import SourceCard from "./SourceCard";

/**
 * ConsumerChat — a continuous multi-turn legal Q&A thread.
 *
 * Receives the initial question + AI answer (from /consumer/ask) as the first
 * conversation turn. Renders a scrollable history + persistent input. Every
 * subsequent user message is POSTed to /consumer/ask-followup with the FULL
 * conversation history so Claude answers in context.
 *
 * Conversation state is kept in component state AND mirrored to sessionStorage
 * under `ll_chat_<sessionKey>` so a tab reload preserves the thread.
 */

// Quick prompts shown under the latest assistant reply. Kept generic so they
// fit any practice area; the actual text is templatized with the user's category.
const QUICK_PROMPTS = [
    { id: "simpler", text: "Explain that in simpler terms." },
    { id: "ask-lawyer", text: "What should I ask a lawyer about this?" },
    { id: "documents", text: "What documents will I need to gather?" },
];

// Markdown renderer with minimal, brand-aligned style overrides. Avoids the
// @tailwindcss/typography dep — we only need a small subset of element styling.
const MD_COMPONENTS = {
    h1: ({ node, ...p }) => <h3 className="font-serif font-semibold text-base text-foreground mt-3 mb-2 first:mt-0" {...p} />,
    h2: ({ node, ...p }) => <h3 className="font-serif font-semibold text-base text-foreground mt-3 mb-1.5 first:mt-0" {...p} />,
    h3: ({ node, ...p }) => <h4 className="font-serif font-semibold text-sm text-foreground mt-2.5 mb-1 first:mt-0" {...p} />,
    p: ({ node, ...p }) => <p className="text-sm leading-relaxed text-foreground mb-2 last:mb-0" {...p} />,
    ul: ({ node, ...p }) => <ul className="list-disc pl-5 mb-2 space-y-1 marker:text-primary/60" {...p} />,
    ol: ({ node, ...p }) => <ol className="list-decimal pl-5 mb-2 space-y-1 marker:text-primary/60" {...p} />,
    li: ({ node, ...p }) => <li className="text-sm leading-relaxed text-foreground" {...p} />,
    strong: ({ node, ...p }) => <strong className="font-semibold text-foreground" {...p} />,
    em: ({ node, ...p }) => <em className="italic" {...p} />,
    a: ({ node, ...p }) => <a className="text-primary hover:underline" target="_blank" rel="noopener noreferrer" {...p} />,
    hr: ({ node, ...p }) => <hr className="my-3 border-border" {...p} />,
    code: ({ node, inline, ...p }) => inline
        ? <code className="px-1 py-0.5 rounded bg-muted text-[0.85em] font-mono" {...p} />
        : <code className="block p-2 rounded bg-muted text-xs font-mono overflow-x-auto" {...p} />,
};

function formatAssistantText(text) {
    if (!text) return null;
    return (
        <div className="text-sm" data-testid="assistant-markdown">
            <ReactMarkdown components={MD_COMPONENTS}>{text}</ReactMarkdown>
        </div>
    );
}

// Browser TTS: strip markdown to plain text so SpeechSynthesis doesn't read
// stars, hashes, and link brackets out loud.
function plainTextForSpeech(md) {
    if (!md) return "";
    return md
        .replace(/```[\s\S]*?```/g, " ")     // fenced code
        .replace(/`([^`]*)`/g, "$1")          // inline code
        .replace(/\[(.*?)\]\((.*?)\)/g, "$1") // [text](url)
        .replace(/!\[(.*?)\]\((.*?)\)/g, "$1")
        .replace(/^[#>\-*\d.\s]+/gm, "")       // headers, blockquotes, list bullets
        .replace(/[*_~]/g, "")                 // emphasis
        .replace(/\[S\d+\]/g, "")             // source tags
        .replace(/\n{2,}/g, ". ")
        .replace(/\s+/g, " ")
        .trim();
}

/**
 * ListenButton — uses the browser's built-in SpeechSynthesis to read an
 * assistant reply aloud. Zero backend cost. Stops any other in-flight
 * utterance before starting so two messages don't overlap.
 */
function ListenButton({ text, testid }) {
    const [speaking, setSpeaking] = useState(false);
    const speak = () => {
        if (typeof window === "undefined" || !window.speechSynthesis) return;
        if (speaking) {
            window.speechSynthesis.cancel();
            setSpeaking(false);
            return;
        }
        window.speechSynthesis.cancel(); // stop any other message reading
        const utter = new SpeechSynthesisUtterance(plainTextForSpeech(text));
        utter.rate = 1.0;
        utter.pitch = 1.0;
        // Prefer a US English voice if available — voices load async on some browsers.
        const voices = window.speechSynthesis.getVoices?.() || [];
        const enUs = voices.find((v) => /en[-_]US/i.test(v.lang)) || voices.find((v) => /^en/i.test(v.lang));
        if (enUs) utter.voice = enUs;
        utter.onend = () => setSpeaking(false);
        utter.onerror = () => setSpeaking(false);
        window.speechSynthesis.speak(utter);
        setSpeaking(true);
    };
    // Cancel any in-flight speech on unmount so navigating away stops audio.
    useEffect(() => () => {
        try { window.speechSynthesis?.cancel(); } catch { /* noop */ }
    }, []);
    if (typeof window !== "undefined" && !window.speechSynthesis) return null;
    return (
        <button
            type="button"
            onClick={speak}
            title={speaking ? "Stop listening" : "Listen to this reply"}
            className={`inline-flex items-center gap-1 text-[11px] rounded-full border px-2 py-0.5 transition-colors ${speaking
                ? "border-primary bg-primary text-primary-foreground"
                : "border-border text-muted-foreground hover:bg-accent/50 hover:text-foreground"}`}
            data-testid={testid}
            data-speaking={speaking ? "true" : "false"}
        >
            {speaking ? <VolumeX className="h-3 w-3" /> : <Volume2 className="h-3 w-3" />}
            {speaking ? "Stop" : "Listen"}
        </button>
    );
}

export default function ConsumerChat({
    initialQuestion,
    initialAnswer,
    initialSources,
    initialProvidersSucceeded,
    initialConfidenceSignal,
    categorySlug,
    categoryName,
    zipCode,
    countyName,
    stateCode,
    sessionKey = "default",
}) {
    const storageKey = `ll_chat_${sessionKey}`;

    // Restore chat from sessionStorage on mount, or seed it with the initial Q&A.
    // Each message:
    //   { role: 'user' | 'assistant', content: str,
    //     sources?: [...], providersSucceeded?: [...], confidenceSignal?: str }
    // Sources/confidence are only attached to assistant turns and rendered
    // by SourceCard under the bubble.
    const [messages, setMessages] = useState(() => {
        try {
            const raw = sessionStorage.getItem(storageKey);
            if (raw) {
                const parsed = JSON.parse(raw);
                if (Array.isArray(parsed) && parsed.length > 0) return parsed;
            }
        } catch { /* ignore corrupted state */ }
        return [
            { role: "user", content: initialQuestion },
            {
                role: "assistant",
                content: initialAnswer,
                sources: initialSources || [],
                providersSucceeded: initialProvidersSucceeded || [],
                confidenceSignal: initialConfidenceSignal || null,
            },
        ];
    });
    const [draft, setDraft] = useState("");
    const [sending, setSending] = useState(false);
    const [error, setError] = useState("");
    // Attachments staged for the NEXT user turn. Each item is the metadata
    // returned by POST /chat/upload — keep this small (max 3) and clear
    // after send.
    const [attachments, setAttachments] = useState([]);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);
    const MAX_FILES = 3;
    const MAX_BYTES = 10 * 1024 * 1024; // 10 MB
    const ACCEPT = ".jpg,.jpeg,.png,.webp,.heic,.heif,.pdf,.docx,.txt,image/*,application/pdf,text/plain";
    // Research mode = formal legal memo with citations. Default OFF so the
    // assistant talks like a human. Persisted per-session so a user who
    // flips it on for a conversation keeps the toggle until they leave.
    const [researchMode, setResearchMode] = useState(() => {
        try {
            return sessionStorage.getItem(`${storageKey}_rm`) === "1";
        } catch { return false; }
    });
    const listRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        try { sessionStorage.setItem(`${storageKey}_rm`, researchMode ? "1" : "0"); } catch { /* quota */ }
    }, [researchMode, storageKey]);

    // Persist + autoscroll on every message change.
    useEffect(() => {
        try { sessionStorage.setItem(storageKey, JSON.stringify(messages)); } catch { /* quota */ }
        // Defer scroll to next tick so the new DOM node is laid out.
        requestAnimationFrame(() => {
            if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
        });
    }, [messages, storageKey]);

    const send = async (overrideText) => {
        const text = (overrideText ?? draft).trim();
        // Allow sending with text OR with at least one attachment.
        if ((!text && attachments.length === 0) || sending) return;
        setError("");
        // User-visible turn shows the typed text plus a brief mention of any
        // attachments so the conversation log makes sense without thumbnails.
        const userVisible = attachments.length
            ? `${text || "(attached files for review)"}\n\n📎 ${attachments.map((a) => a.filename).join(", ")}`
            : text;
        const next = [...messages, { role: "user", content: userVisible, attachments }];
        setMessages(next);
        setDraft("");
        const sentAttachments = attachments;
        setAttachments([]);
        setSending(true);
        try {
            // Backend strips sources/confidence fields from the conversation
            // history payload — we send only role + content (the LLM doesn't
            // need our metadata).
            const historyForBackend = next.map((m) => ({ role: m.role, content: m.content }));
            const { data } = await api.post("/consumer/ask-followup", {
                messages: historyForBackend,
                category_slug: categorySlug,
                zip_code: zipCode,
                county_name: countyName,
                state_code: stateCode,
                research_mode: researchMode,
                attachments: sentAttachments.length ? sentAttachments.map((a) => ({
                    file_id: a.file_id,
                    r2_key: a.r2_key,
                    filename: a.filename,
                    mime_type: a.mime_type,
                    size: a.size,
                })) : undefined,
            });
            setMessages((prev) => [...prev, {
                role: "assistant",
                content: data.reply,
                sources: data.sources || [],
                providersSucceeded: data.providers_succeeded || [],
                confidenceSignal: data.confidence_signal || null,
                modelUsed: data.model_used || null,
            }]);
        } catch (e) {
            setError(e?.response?.data?.detail || "Couldn't reach the assistant. Please try again.");
            // Roll back the optimistic user message so they can retry / edit.
            setMessages((prev) => prev.slice(0, -1));
            setDraft(text);
            setAttachments(sentAttachments);
        } finally {
            setSending(false);
            inputRef.current?.focus();
        }
    };

    const onPickFiles = async (e) => {
        const picked = Array.from(e.target.files || []);
        e.target.value = ""; // reset so user can re-pick the same file
        if (!picked.length) return;
        const room = MAX_FILES - attachments.length;
        if (room <= 0) {
            setError(`You can attach up to ${MAX_FILES} files per message.`);
            return;
        }
        const tooBig = picked.find((f) => f.size > MAX_BYTES);
        if (tooBig) {
            setError(`"${tooBig.name}" is larger than 10 MB. Pick a smaller file.`);
            return;
        }
        setError("");
        setUploading(true);
        try {
            const newOnes = [];
            for (const f of picked.slice(0, room)) {
                const form = new FormData();
                form.append("file", f);
                const { data } = await api.post("/chat/upload", form, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                newOnes.push(data);
            }
            setAttachments((prev) => [...prev, ...newOnes]);
        } catch (err) {
            setError(err?.response?.data?.detail || "File upload failed. Please try again.");
        } finally {
            setUploading(false);
        }
    };

    const removeAttachment = (file_id) => {
        setAttachments((prev) => prev.filter((a) => a.file_id !== file_id));
    };

    const onKeyDown = (e) => {
        // Enter sends, Shift+Enter inserts newline.
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            send();
        }
    };

    const handleQuickPrompt = (prompt) => send(prompt);

    // Index of the LAST assistant message — used to attach the hint + suggestion
    // chips only to the most recent reply.
    const lastAssistantIdx = (() => {
        for (let i = messages.length - 1; i >= 0; i--) {
            if (messages[i].role === "assistant") return i;
        }
        return -1;
    })();

    return (
        <div className="ll-card" data-testid="consumer-chat">
            <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
                <div className="flex items-center gap-2">
                    <FileText className="h-5 w-5 text-primary" />
                    <h2 className="font-serif text-xl font-semibold">
                        Legal information chat{categoryName ? ` — ${categoryName}` : ""}
                    </h2>
                </div>
                <button
                    type="button"
                    role="switch"
                    aria-checked={researchMode}
                    onClick={() => setResearchMode((v) => !v)}
                    title={researchMode
                        ? "Research mode is ON: formal answers with explicit citations."
                        : "Conversational mode (default). Click for formal legal-research answers with citations."}
                    className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${researchMode
                        ? "border-primary bg-primary text-primary-foreground hover:bg-primary/90"
                        : "border-border bg-background text-foreground hover:bg-accent/50"}`}
                    data-testid="chat-research-mode-toggle"
                    data-active={researchMode ? "true" : "false"}
                >
                    <BookOpen className="h-3.5 w-3.5" />
                    Research mode {researchMode ? "ON" : "OFF"}
                </button>
            </div>

            {/* Conversation history */}
            <div
                ref={listRef}
                className="space-y-4 max-h-[60vh] overflow-y-auto pr-1 pb-2"
                data-testid="chat-history"
                aria-live="polite"
            >
                {messages.map((m, i) => (
                    <div key={i} data-testid={`chat-turn-${i}`} data-role={m.role}>
                        {m.role === "user" ? (
                            <div className="flex justify-end">
                                <div className="max-w-[85%] rounded-2xl rounded-br-sm bg-primary text-primary-foreground px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap" data-testid={`chat-user-${i}`}>
                                    {m.content}
                                </div>
                            </div>
                        ) : (
                            <div className="flex justify-start">
                                <div className="max-w-[95%] rounded-2xl rounded-bl-sm bg-accent/40 border border-border px-4 py-3" data-testid={`chat-assistant-${i}`}>
                                    {formatAssistantText(m.content)}
                                    {/* Listen button — browser TTS, no backend cost. */}
                                    <div className="mt-2 flex justify-end">
                                        <ListenButton text={m.content} testid={`chat-listen-${i}`} />
                                    </div>
                                    {/* Citation chips for this assistant turn. */}
                                    {(m.sources?.length > 0 || m.confidenceSignal) && (
                                        <SourceCard
                                            sources={m.sources || []}
                                            providersSucceeded={m.providersSucceeded || []}
                                            confidenceSignal={m.confidenceSignal}
                                            testid={`source-card-${i}`}
                                        />
                                    )}
                                    {/* Hint + suggestion chips only on the most recent assistant reply. */}
                                    {i === lastAssistantIdx && (
                                        <div className="mt-3 pt-3 border-t border-border/60">
                                            <div className="text-xs text-muted-foreground mb-2.5 flex items-center gap-1.5" data-testid="chat-followup-hint">
                                                <Sparkles className="h-3 w-3" />
                                                You can ask another question or clarify your situation.
                                            </div>
                                            <div className="flex flex-wrap gap-2" data-testid="chat-quick-prompts">
                                                {QUICK_PROMPTS.map((q) => (
                                                    <button
                                                        key={q.id}
                                                        type="button"
                                                        onClick={() => handleQuickPrompt(q.text)}
                                                        disabled={sending}
                                                        className="text-xs rounded-full border border-primary/30 bg-primary/5 text-primary px-3 py-1.5 font-medium hover:bg-primary/10 transition-colors disabled:opacity-60"
                                                        data-testid={`chat-quick-prompt-${q.id}`}
                                                    >
                                                        {q.text}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                ))}

                {sending && (
                    <div className="flex justify-start" data-testid="chat-typing">
                        <div className="rounded-2xl rounded-bl-sm bg-accent/40 border border-border px-4 py-3 inline-flex items-center gap-2 text-sm text-muted-foreground">
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            Thinking…
                        </div>
                    </div>
                )}
            </div>

            {error && (
                <div className="mt-3 text-sm text-destructive" data-testid="chat-error">{error}</div>
            )}

            {/* Persistent input — always available, never auto-collapses. */}
            <div className="mt-4 pt-4 border-t border-border">
                {/* Staged attachment chips */}
                {attachments.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-3" data-testid="chat-attachments">
                        {attachments.map((a) => {
                            const isImg = (a.mime_type || "").startsWith("image/");
                            return (
                                <div
                                    key={a.file_id}
                                    className="inline-flex items-center gap-2 max-w-[280px] rounded-full border border-border bg-accent/40 pl-2 pr-1 py-1 text-xs"
                                    data-testid={`chat-attachment-${a.file_id}`}
                                >
                                    {isImg ? <ImageIcon className="h-3.5 w-3.5 text-primary flex-shrink-0" /> : <FileIcon className="h-3.5 w-3.5 text-primary flex-shrink-0" />}
                                    <span className="truncate" title={a.filename}>{a.filename}</span>
                                    <button
                                        type="button"
                                        onClick={() => removeAttachment(a.file_id)}
                                        className="rounded-full p-1 hover:bg-destructive/15 text-muted-foreground hover:text-destructive flex-shrink-0"
                                        aria-label={`Remove ${a.filename}`}
                                        data-testid={`chat-attachment-remove-${a.file_id}`}
                                    >
                                        <X className="h-3 w-3" />
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                )}

                <div className="flex items-end gap-2">
                    {/* Hidden native file input */}
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept={ACCEPT}
                        multiple
                        className="hidden"
                        onChange={onPickFiles}
                        data-testid="chat-file-input"
                    />
                    <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading || sending || attachments.length >= MAX_FILES}
                        title={attachments.length >= MAX_FILES
                            ? `Max ${MAX_FILES} files per message`
                            : "Attach a photo, PDF, DOCX, or TXT (10 MB max)"}
                        className="rounded-full border border-border bg-background p-3 hover:bg-accent/50 disabled:opacity-50 transition-colors flex-shrink-0"
                        aria-label="Attach file"
                        data-testid="chat-attach-btn"
                    >
                        {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
                    </button>
                    <textarea
                        ref={inputRef}
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        onKeyDown={onKeyDown}
                        rows={2}
                        placeholder={attachments.length
                            ? "Add a question about the attached files… (Enter to send)"
                            : "Ask a follow-up… (Enter to send, Shift+Enter for new line)"}
                        className="ll-input resize-none flex-1"
                        disabled={sending}
                        data-testid="chat-input"
                    />
                    <button
                        type="button"
                        onClick={() => send()}
                        disabled={sending || (!draft.trim() && attachments.length === 0)}
                        className="rounded-full bg-primary text-primary-foreground p-3 hover:bg-primary/90 disabled:opacity-60 transition-colors flex-shrink-0"
                        aria-label="Send"
                        data-testid="chat-send-btn"
                    >
                        {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    </button>
                </div>
                <div className="mt-2 text-[11px] text-muted-foreground">
                    General info — not legal advice. Files: JPG, PNG, WEBP, HEIC, PDF, DOCX, TXT · up to 10 MB each · {MAX_FILES} per message.
                </div>
            </div>
        </div>
    );
}
